import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Eye, Globe, FileText, Loader2, Search, Bold, Italic, Underline, List, ListOrdered, Quote,
  Heading1, Heading2, Heading3, Link as LinkIcon, Undo, Redo, RemoveFormatting, Image as ImageIcon, Upload,
  MousePointerClick, ExternalLink,
} from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExtension from '@tiptap/extension-link';
import ImageExtension from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import UnderlineExtension from '@tiptap/extension-underline';
import { mediaUrl } from '@/lib/mediaUrl';
import { publicPagePath } from '@/lib/pagePaths';

const generateSlug = (title) => title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

const EMPTY = {
  title: '', slug: '', excerpt: '', seo_title: '', seo_description: '', status: 'draft', show_in_navigation: 'none',
  navigation_order: 0, template: 'default', nav_label: '', featured_image_url: '', featured_image_alt: '',
  cta_primary_label: '', cta_primary_url: '', cta_secondary_label: '', cta_secondary_url: '',
};

const useUpload = (api) => async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const r = await api.post('/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return r.data.url;
};

const ImageDialog = ({ open, onClose, onInsert, upload }) => {
  const [src, setSrc] = useState('');
  const [alt, setAlt] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setSrc(''); setAlt(''); } }, [open]);
  const pick = async (file) => {
    setBusy(true);
    try { setSrc(await upload(file)); } catch { toast.error('Upload failed'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="insert-image-dialog">
        <DialogHeader><DialogTitle>Insert image</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input value={src} onChange={(e) => setSrc(e.target.value)} placeholder="Upload or paste an image link" data-testid="insert-image-url" />
            <label className="shrink-0 inline-flex items-center gap-1.5 px-3 h-9 rounded-md border text-sm cursor-pointer hover:bg-slate-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Upload
              <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && pick(e.target.files[0])} data-testid="insert-image-upload" />
            </label>
          </div>
          {src && <img src={mediaUrl(src)} alt="" className="h-32 w-full object-cover rounded-md border" />}
          <div className="space-y-1">
            <Label className="text-sm">Alt text (describe the image for screen readers)</Label>
            <Input value={alt} onChange={(e) => setAlt(e.target.value)} placeholder="e.g. A support worker chatting with a resident" data-testid="insert-image-alt" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!src || !alt.trim()} onClick={() => { onInsert(mediaUrl(src), alt.trim()); onClose(); }} className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="insert-image-confirm">Insert</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MenuBar = ({ editor, onImage }) => {
  if (!editor) return null;
  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };
  const btn = (active) => `p-1.5 rounded transition-colors ${active ? 'bg-[#20acf6]/20 text-[#20acf6]' : 'text-[#1e293b]/60 hover:bg-[#f8fafc] hover:text-[#1e293b]'}`;
  const items = [
    [Heading1, 'Heading 1', () => editor.chain().focus().toggleHeading({ level: 1 }).run(), editor.isActive('heading', { level: 1 })],
    [Heading2, 'Heading 2', () => editor.chain().focus().toggleHeading({ level: 2 }).run(), editor.isActive('heading', { level: 2 })],
    [Heading3, 'Heading 3', () => editor.chain().focus().toggleHeading({ level: 3 }).run(), editor.isActive('heading', { level: 3 })],
    null,
    [Bold, 'Bold', () => editor.chain().focus().toggleBold().run(), editor.isActive('bold')],
    [Italic, 'Italic', () => editor.chain().focus().toggleItalic().run(), editor.isActive('italic')],
    [Underline, 'Underline', () => editor.chain().focus().toggleUnderline().run(), editor.isActive('underline')],
    null,
    [List, 'Bullet List', () => editor.chain().focus().toggleBulletList().run(), editor.isActive('bulletList')],
    [ListOrdered, 'Numbered List', () => editor.chain().focus().toggleOrderedList().run(), editor.isActive('orderedList')],
    [Quote, 'Blockquote', () => editor.chain().focus().toggleBlockquote().run(), editor.isActive('blockquote')],
    null,
    [LinkIcon, 'Add Link', addLink, editor.isActive('link')],
    [ImageIcon, 'Insert Image', onImage, false, 'editor-insert-image-btn'],
    null,
    [Undo, 'Undo', () => editor.chain().focus().undo().run(), false],
    [Redo, 'Redo', () => editor.chain().focus().redo().run(), false],
    [RemoveFormatting, 'Clear Formatting', () => editor.chain().focus().clearNodes().unsetAllMarks().run(), false],
  ];
  return (
    <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-[#f8fafc] bg-[#F8FAFC] rounded-t-xl" data-testid="editor-toolbar">
      {items.map((it, i) => {
        if (!it) return <div key={`sep-${i}`} className="w-px h-5 bg-slate-200 mx-1" />;
        const [Icon, title, onClick, active, testId] = it;
        return <button key={title} type="button" onClick={onClick} className={btn(active)} title={title} data-testid={testId}><Icon className="w-4 h-4" /></button>;
      })}
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm text-[#1e293b]/70">{label}</Label>
    {children}
    {hint && <p className="text-xs text-[#1e293b]/40">{hint}</p>}
  </div>
);

const CmsPageEditor = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { pageId } = useParams();
  const isEditing = !!pageId;
  const upload = useUpload(api);
  const backupKey = `cms-page-backup-${pageId || 'new'}`;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [slugManual, setSlugManual] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [loadedHtml, setLoadedHtml] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [imageOpen, setImageOpen] = useState(false);
  const [heroBusy, setHeroBusy] = useState(false);
  const serverUpdatedAt = useRef(null);

  const editor = useEditor({
    extensions: [StarterKit, UnderlineExtension, LinkExtension.configure({ openOnClick: false }), ImageExtension,
      Placeholder.configure({ placeholder: 'Write your page content here...' })],
    content: '',
    onUpdate: () => setDirty(true),
    editorProps: { attributes: { class: 'cms-content prose prose-lg max-w-none min-h-[300px] p-4 focus:outline-none text-[#1e293b]' } },
  });

  // Content arrives before or after the editor is ready — apply it once both exist
  useEffect(() => {
    if (editor && loadedHtml !== null) { editor.commands.setContent(loadedHtml || '', { emitUpdate: false }); setLoadedHtml(null); }
  }, [editor, loadedHtml]);

  const offerBackup = useCallback((serverForm, serverHtml) => {
    try {
      const b = JSON.parse(localStorage.getItem(backupKey) || 'null');
      if (!b || (serverUpdatedAt.current && b.savedAt < serverUpdatedAt.current)) return;
      if (b.html === serverHtml && JSON.stringify(b.form) === JSON.stringify(serverForm)) return;
      toast('Unsaved changes found', {
        description: `From ${new Date(b.savedAt).toLocaleString('en-GB')}`, duration: 15000,
        action: { label: 'Restore', onClick: () => { setForm(b.form); setLoadedHtml(b.html); setDirty(true); } },
      });
    } catch { /* ignore corrupt backup */ }
  }, [backupKey]);

  useEffect(() => {
    if (!isEditing) { offerBackup(EMPTY, ''); return; }
    api.get(`/cms/pages/${pageId}`).then((res) => {
      const next = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, res.data[k] ?? EMPTY[k]]));
      serverUpdatedAt.current = res.data.updated_at ? new Date(res.data.updated_at).getTime() : null;
      setForm(next);
      setLoadedHtml(res.data.content || '');
      setSlugManual(true);
      offerBackup(next, res.data.content || '');
    }).catch(() => { toast.error('Failed to load page'); navigate('/admin/pages'); }).finally(() => setLoading(false));
  }, [pageId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Local safety copy of unsaved work, written shortly after each change
  useEffect(() => {
    if (!dirty || !editor) return undefined;
    const t = setTimeout(() => localStorage.setItem(backupKey, JSON.stringify({ form, html: editor.getHTML(), savedAt: Date.now() })), 800);
    return () => clearTimeout(t);
  });

  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const handleChange = (field, value) => { setForm((prev) => ({ ...prev, [field]: value })); setDirty(true); };
  const handleTitleChange = (e) => {
    const title = e.target.value;
    setForm((prev) => ({ ...prev, title, slug: slugManual ? prev.slug : generateSlug(title) }));
    setDirty(true);
  };
  const uploadHero = async (file) => {
    setHeroBusy(true);
    try { handleChange('featured_image_url', await upload(file)); toast.success('Image uploaded'); } catch { toast.error('Upload failed'); } finally { setHeroBusy(false); }
  };

  const handleSave = async (publishAfterSave = false) => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.slug.trim()) { toast.error('Slug is required'); return; }
    if (form.featured_image_url && !form.featured_image_alt.trim()) { toast.error('Add alt text for the main image'); return; }
    setSaving(true);
    const payload = { ...form, content: editor ? editor.getHTML() : '', status: publishAfterSave ? 'published' : form.status };
    try {
      if (isEditing) await api.put(`/cms/pages/${pageId}`, payload);
      else await api.post('/cms/pages', payload);
      localStorage.removeItem(backupKey);
      setDirty(false);
      toast.success(isEditing ? 'Page updated' : 'Page created');
      navigate('/admin/pages');
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Failed to save page — your changes are kept on this device');
    } finally { setSaving(false); }
  };

  const goBack = () => { if (!dirty || window.confirm('You have unsaved changes. Leave without saving?')) navigate('/admin/pages'); };

  if (loading) {
    return (
      <DashboardLayout requiredRoles={['super_admin', 'content_manager']}>
        <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div>
      </DashboardLayout>
    );
  }

  const inputCls = 'border-[#e2e8f0] focus:border-[#20acf6] focus:ring-[#20acf6]';
  return (
    <DashboardLayout requiredRoles={['super_admin', 'content_manager']}>
      <div className="space-y-6" data-testid="cms-page-editor">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={goBack} data-testid="back-to-pages"><ArrowLeft className="h-5 w-5" /></Button>
            <div>
              <h1 className="text-2xl font-bold text-[#2a3e5d]">{isEditing ? 'Edit Page' : 'New Page'}</h1>
              <p className="text-sm text-[#1e293b]/60" data-testid="editor-save-state">{dirty ? 'Unsaved changes (backed up on this device)' : 'All changes saved'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isEditing && form.status === 'published' && (
              <Button variant="ghost" asChild><a href={publicPagePath(form.slug)} target="_blank" rel="noopener noreferrer" data-testid="view-live-page"><ExternalLink className="h-4 w-4 mr-2" />View live</a></Button>
            )}
            <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} className="border-[#20acf6] text-[#20acf6] hover:bg-[#f8fafc]" data-testid="save-draft-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}Save
            </Button>
            <Button onClick={() => handleSave(true)} disabled={saving} className="bg-[#20acf6] hover:bg-[#3bb8f7] text-white" data-testid="publish-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}Publish
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="border-[#e2e8f0]">
              <CardContent className="p-6 space-y-4">
                <Field label="Page heading">
                  <Input value={form.title} onChange={handleTitleChange} placeholder="e.g. About Us, Funding Information, FAQs" className={`text-lg ${inputCls}`} data-testid="page-title-input" />
                </Field>
                <Field label="Intro line (shown under the heading)">
                  <Textarea value={form.excerpt} onChange={(e) => handleChange('excerpt', e.target.value)} rows={2} placeholder="Short summary of the page..." className={inputCls} data-testid="page-excerpt" />
                </Field>
                <Field label={<span className="flex items-center gap-1"><Globe className="h-3 w-3" /> URL slug</span>} hint={`Public address: ${publicPagePath(form.slug || 'your-page')}`}>
                  <Input value={form.slug} onChange={(e) => { setSlugManual(true); handleChange('slug', e.target.value); }} placeholder="about-us" className={inputCls} data-testid="page-slug-input" />
                </Field>
              </CardContent>
            </Card>

            <Card className="border-[#e2e8f0]">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2 text-[#1e293b]"><FileText className="h-4 w-4 text-[#20acf6]" />Content</CardTitle></CardHeader>
              <CardContent className="p-6 pt-2">
                <div className="border border-[#e2e8f0] rounded-xl overflow-hidden" data-testid="page-content-editor">
                  <MenuBar editor={editor} onImage={() => setImageOpen(true)} />
                  <EditorContent editor={editor} />
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#e2e8f0]">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2 text-[#1e293b]"><Search className="h-4 w-4 text-[#02d1d4]" />SEO Settings</CardTitle></CardHeader>
              <CardContent className="p-6 pt-2 space-y-4">
                <Field label="Meta title" hint={`${(form.seo_title || form.title).length}/60 characters`}>
                  <Input value={form.seo_title} onChange={(e) => handleChange('seo_title', e.target.value)} placeholder={form.title || 'Page title will be used'} className={inputCls} data-testid="seo-title-input" />
                </Field>
                <Field label="Meta description" hint={`${form.seo_description.length}/160 characters`}>
                  <Textarea value={form.seo_description} onChange={(e) => handleChange('seo_description', e.target.value)} rows={3} placeholder="Brief description for search engines..." className={inputCls} data-testid="seo-description-input" />
                </Field>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border-[#e2e8f0]">
              <CardHeader className="pb-2"><CardTitle className="text-base text-[#1e293b]">Page Settings</CardTitle></CardHeader>
              <CardContent className="p-6 pt-2 space-y-4">
                <Field label="Status">
                  <Select value={form.status} onValueChange={(v) => handleChange('status', v)}>
                    <SelectTrigger data-testid="page-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="published">Published</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Show in navigation">
                  <Select value={form.show_in_navigation} onValueChange={(v) => handleChange('show_in_navigation', v)}>
                    <SelectTrigger data-testid="page-nav-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not in navigation</SelectItem><SelectItem value="header">Header only</SelectItem>
                      <SelectItem value="footer">Footer only</SelectItem><SelectItem value="both">Header & Footer</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Menu label" hint="Short name shown in the menu. Leave blank to use the page heading.">
                  <Input value={form.nav_label} onChange={(e) => handleChange('nav_label', e.target.value)} placeholder="e.g. About Us" className={inputCls} data-testid="page-nav-label" />
                </Field>
                <Field label="Navigation order">
                  <Input type="number" min={0} value={form.navigation_order} onChange={(e) => handleChange('navigation_order', parseInt(e.target.value, 10) || 0)} className={inputCls} data-testid="page-nav-order" />
                </Field>
              </CardContent>
            </Card>

            <Card className="border-[#e2e8f0]">
              <CardHeader className="pb-2"><CardTitle className="text-base text-[#1e293b] flex items-center gap-2"><ImageIcon className="h-4 w-4 text-[#20acf6]" />Main image</CardTitle></CardHeader>
              <CardContent className="p-6 pt-2 space-y-3">
                {form.featured_image_url && <img src={mediaUrl(form.featured_image_url)} alt={form.featured_image_alt} className="h-36 w-full object-cover rounded-lg border" data-testid="hero-image-preview" />}
                <div className="flex gap-2">
                  <Input value={form.featured_image_url} onChange={(e) => handleChange('featured_image_url', e.target.value)} placeholder="Upload or paste a link" className={inputCls} data-testid="hero-image-url" />
                  <label className="shrink-0 inline-flex items-center gap-1.5 px-3 h-9 rounded-md border text-sm cursor-pointer hover:bg-slate-50">
                    {heroBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadHero(e.target.files[0])} data-testid="hero-image-upload" />
                  </label>
                </div>
                <Field label="Alt text">
                  <Input value={form.featured_image_alt} onChange={(e) => handleChange('featured_image_alt', e.target.value)} placeholder="Describe the image" className={inputCls} data-testid="hero-image-alt" />
                </Field>
              </CardContent>
            </Card>

            <Card className="border-[#e2e8f0]">
              <CardHeader className="pb-2"><CardTitle className="text-base text-[#1e293b] flex items-center gap-2"><MousePointerClick className="h-4 w-4 text-[#20acf6]" />Buttons</CardTitle></CardHeader>
              <CardContent className="p-6 pt-2 space-y-3">
                {[['primary', 'Main button'], ['secondary', 'Second button']].map(([k, l]) => (
                  <div key={k} className="grid grid-cols-2 gap-2">
                    <Field label={`${l} text`}><Input value={form[`cta_${k}_label`]} onChange={(e) => handleChange(`cta_${k}_label`, e.target.value)} className={inputCls} data-testid={`cta-${k}-label`} /></Field>
                    <Field label="Link"><Input value={form[`cta_${k}_url`]} onChange={(e) => handleChange(`cta_${k}_url`, e.target.value)} placeholder="/pricing" className={inputCls} data-testid={`cta-${k}-url`} /></Field>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <ImageDialog open={imageOpen} onClose={() => setImageOpen(false)} upload={upload}
        onInsert={(src, alt) => { editor?.chain().focus().setImage({ src, alt }).run(); setDirty(true); }} />
    </DashboardLayout>
  );
};

export default CmsPageEditor;
